import React, { useState, useEffect } from 'react';
import { API_URL } from '../backend_url.js';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Chip,
  Grid,
  Card,
  CardContent,
  Container,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { Link as RouterLink } from 'react-router-dom';

const AdminPage = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [user] = useAuthState(auth);
  const [totalForms, setTotalForms] = useState(0);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [protectedForms, setProtectedForms] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await user.getIdToken();
        
        // Fetch forms
        const formsResponse = await axios.get( 'https://harshanpvtserver.duckdns.org/form-pulse/my-forms', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setForms(formsResponse.data.forms);

        // Fetch stats
        const statsResponse = await axios.get('https://harshanpvtserver.duckdns.org/form-pulse/admin-stats', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // console.log('Stats:', statsResponse.data);
        // setStats(statsResponse.data);
        // setStats({
        //   total_forms: statsResponse.data.total_forms,
        //   protected_forms: statsResponse.data.protected_forms,
        //   total_submissions: statsResponse.data.total_submissions
        // });
        setTotalForms(statsResponse.data.total_forms);
        setTotalSubmissions(statsResponse.data.total_submissions);
        setProtectedForms(statsResponse.data.protected_forms);
        // console.log('Updated Stats:', stats);

      } catch (err) {
        showSnackbar(err.response?.data?.detail || 'Failed to load data', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCopyLink = async (formName) => {
    try {
      const formLink = `${window.location.origin}/form/${encodeURIComponent(formName)}`;
      await navigator.clipboard.writeText(formLink);
      showSnackbar('Form link copied to clipboard!');
    } catch (err) {
      showSnackbar('Failed to copy link to clipboard', 'error');
    }
  };

  const handleExport = async (formName) => {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(
        `https://harshanpvtserver.duckdns.org/form-pulse/submissions/${formName}/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formName}_submissions.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar('Export started successfully');
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Failed to export submissions', 'error');
    }
  };

  const handleDeleteClick = (form) => {
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = await user.getIdToken();
      await axios.delete(
        `https://harshanpvtserver.duckdns.org/form-pulse/form/${formToDelete.form_name}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForms(forms.filter(f => f.form_name !== formToDelete.form_name));
      setTotalForms(totalForms - 1);
      setTotalSubmissions(totalSubmissions - formToDelete.submissions_count);
      setProtectedForms(protectedForms - (formToDelete.protected ? 1 : 0));

      showSnackbar('Form and all submissions deleted successfully');
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Failed to delete form', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFormToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getFormLink = (formName) => {
    return `${window.location.origin}/form/${encodeURIComponent(formName)}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading your dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            Dashboard
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Manage your forms and track submissions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={8} 
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Total Forms
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {totalForms}
                  </Typography>
                </Box>
                <DescriptionIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={8} 
            sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Proctored Forms
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {protectedForms}
                  </Typography>
                </Box>
                <SecurityIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={8} 
            sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Total Submissions
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {totalSubmissions}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/upload-form"
          size="large"
          sx={{ 
            px: 4,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
            }
          }}
        >
          Create New Form
        </Button>
      </Box>

      {forms.length === 0 ? (
        <Card elevation={8} sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <DescriptionIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              No Forms Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Start creating beautiful forms to collect data from your audience. 
              It's quick and easy to get started!
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/upload-form"
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                }
              }}
            >
              Create Your First Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card elevation={8}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Your Forms
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your forms in one place
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      background: alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 'bold' }}>Form Details</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forms.map((form, index) => (
                    <TableRow 
                      key={form.form_name} 
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {form.form_name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip 
                              label={form.protected ? "Proctored" : "Standard"} 
                              color={form.protected ? "warning" : "success"}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                            <Chip 
                              label={`${form.submissions_count || 0} submissions`} 
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(form.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(form.created_at).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}>
                          <Tooltip title="Open Form">
                            <IconButton
                              component={Link}
                              href={getFormLink(form.form_name)}
                              target="_blank"
                              rel="noopener"
                              color="primary"
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                                }
                              }}
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="View Submissions">
                            <Button
                              component={RouterLink}
                              to={`/submissions/${form.form_name}`}
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              sx={{ borderRadius: 2 }}
                            >
                              View
                            </Button>
                          </Tooltip>
                          
                          <Tooltip title="Copy Form Link">
                            <IconButton
                              onClick={() => handleCopyLink(form.form_name)}
                              color="primary"
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                                }
                              }}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Export to Excel">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleExport(form.form_name)}
                              sx={{ borderRadius: 2 }}
                            >
                              Export
                            </Button>
                          </Tooltip>

                          <Tooltip title="Delete Form">
                            <IconButton
                              onClick={() => handleDeleteClick(form)}
                              color="error"
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: alpha(theme.palette.error.main, 0.1) 
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 8,
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the form <strong>{formToDelete?.form_name}</strong>?
            This will permanently delete the form and all its submissions. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            autoFocus
            sx={{ borderRadius: 2 }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPage;